import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { ArrowRight, CheckCircle2 } from "lucide-react";
export default function DirectConsulting() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    once: true
  });
  const highlights = [{
    label: "15+ years",
    desc: "hospitality commercial strategy"
  }, {
    label: "700+ hotels",
    desc: "commercial distribution & yield"
  }, {
    label: "Works with you directly",
    desc: "no juniors, no black-box bots"
  }];
  return <section id="direct-consulting" data-section-id="direct-consulting" className="relative bg-primary text-primary-foreground py-20 lg:py-28 overflow-hidden border-b border-border">
      {/* Subtle gold ambient glow and texture */}
      <div className="pointer-events-none absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-secondary/20 blur-3xl" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div ref={sectionRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {/* Portrait Column */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <div className="relative w-full max-w-sm sm:max-w-md">
              {/* Outer decorative gold frame border */}
              <div className="relative rounded-2xl overflow-hidden border-2 border-secondary/40 shadow-2xl bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunilsingh-image.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunilsingh-image.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunilsingh-image.jpg-wvc-sizes" alt="Sunil Singh, hotel revenue consultant" loading="lazy" decoding="async" className="w-full aspect-[4/5] object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent" />

                {/* Floating caption badge inside frame */}
                <div className="absolute bottom-4 left-4 right-4 bg-primary/95 backdrop-blur-md p-4 rounded-xl border border-secondary/30 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-base font-bold text-primary-foreground tracking-tight">
                        Sunil Singh
                      </p>
                      <p className="text-xs text-secondary font-mono tracking-wider uppercase mt-0.5">
                        Principal Revenue Consultant
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono tracking-wider bg-secondary/20 text-secondary border border-secondary/40 uppercase">
                      Direct Partner
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Text and CTAs Column */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-secondary/15 border border-secondary/30 rounded-md text-xs font-mono tracking-widest uppercase text-secondary">
              <span>FOUNDER &amp; LEAD STRATEGIST</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-tight">
              Direct Strategic Consulting from Sunil Singh
            </h2>

            {/* Paragraph 1: Visual Lead */}
            <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-secondary leading-snug">
              I don’t hand your hotel&apos;s commercial strategy to an intern or a black-box bot.
            </p>

            {/* Paragraph 2 */}
            <p className="text-base sm:text-lg text-primary-foreground/85 leading-relaxed">
              When you work with HotelRevBaBa, you work directly with me. Over 15 years, I have managed commercial distribution, dynamic pricing, and channel parity for 700+ hotels across independent brands, boutique retreats, and city properties.
            </p>

            {/* Paragraph 3 */}
            <p className="text-base sm:text-lg text-primary-foreground/85 leading-relaxed">
              I audit extranet discounts, fix PMS-channel manager mapping errors, and track pickup pace daily. No generic advice. Just hands-on pricing discipline that protects your net ADR and lifts GOPPAR.
            </p>

            {/* Plain Facts Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-border/40">
              {highlights.map((item, idx) => <div key={idx} className="flex flex-col">
                  <div className="flex items-center gap-1.5 text-secondary font-bold text-base sm:text-lg">
                    <CheckCircle2 className="size-4 shrink-0 text-secondary" />
                    <span>{item.label}</span>
                  </div>
                  <span className="text-xs text-primary-foreground/70 font-mono mt-0.5">
                    {item.desc}
                  </span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
              <Link to="/ContactPage#start-your-revenue-journey" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-secondary text-primary font-semibold text-base rounded-md shadow-md hover:bg-secondary/90 transition-colors duration-200">
                <span>Request a Free Revenue Audit</span>
                <ArrowRight className="size-4" />
              </Link>

              <Link to="/AboutPage" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-border/80 bg-primary/40 hover:bg-primary/80 text-primary-foreground hover:text-secondary font-semibold text-base rounded-md transition-colors duration-200">
                <span>More about Sunil</span>
                <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}